import React, { useState } from "react";
import {
  ArrowLeft,
  FileText,
  Download,
  Eye,
  Activity,
  Droplet,
  Heart,
  Thermometer,
  Scale,
  Ruler,
  User,
  Calendar,
  UserCircle,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetLabReportQuery } from "../../State/Api/GetallappointmentsApiSlice";
import ReportViewer from "../Utility/ReportViewer";

const ViewReports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientUserUniqueID, appointmentID } = useParams();

  const { data, isLoading, isFetching, isSuccess,isError } = useGetLabReportQuery({
    patientUserUniqueID,
    appointmentID,
  });



  const handleBack = () => {
    navigate(-1);
  };

  const [showViewReportModal, setShowViewReportModal] = useState(false);

  const [selectedTest, setSelectedTest] = useState(0);

  const [fileUrl, setFileUrl] = useState("");
  const [summary, setSummary] = useState("");

  // CSS Variables for gradient colors
  const gradientStyles = `
    :root {
      --color-secondary: #10b981;
      --color-secondary-mid: #059669;
      --color-secondary-dark: #047857;
      --color-dark-light: #4b5563;
      --color-dark: #374151;
      --color-dark-deep: #1f2937;
    }
    
    .gradient-button {
      background: linear-gradient(130deg, var(--color-secondary) 0%, var(--color-secondary-mid) 50%, var(--color-secondary-dark) 100%);
      color: white;
      border: none;
      box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
      transition: all 0.3s ease;
    }
    
    .gradient-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
    }
    
    .gradient-dark-button {
      background: linear-gradient(135deg, var(--color-dark-light) 0%, var(--color-dark) 50%, var(--color-dark-deep) 100%);
      color: white;
      border: none;
      box-shadow: 0 4px 15px rgba(75, 85, 99, 0.3);
      transition: all 0.3s ease;
    }
    
    .gradient-dark-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(75, 85, 99, 0.4);
    }
    
    .medical-card {
      background: white;
      border-radius: 16px;
      border: 1px solid rgba(229, 231, 235, 0.8);
      transition: all 0.3s ease;
    }
    
    .medical-card:hover {
      transform: translateY(-4px);
      box-shadow: rgba(57, 255, 73, 0.16) 0px 3px 6px, rgba(57, 255, 73, 0.23) 0px 3px 6px;
    }
    
    .vital-card {
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
      border-radius: 12px;
      border: 1px solid rgba(226, 232, 240, 0.8);
      transition: all 0.3s ease;
    }
    
    .vital-card:hover {
      background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    }
    
    .test-card {
      background: white;
      border-radius: 12px;
      border: 1px solid rgba(229, 231, 235, 0.6);
      transition: all 0.3s ease;
    }
    
    .test-card:hover {
      border-color: var(--color-secondary);
      box-shadow: 0 8px 30px rgba(16, 185, 129, 0.1);
    }
    
    .status-safe {
      background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
      color: #166534;
      border: 1px solid #86efac;
    }
    
    .status-warning {
      background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
      color: #92400e;
      border: 1px solid #fbbf24;
    }
    
    .status-danger {
      background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
      color: #991b1b;
      border: 1px solid #f87171;
    }
    
    .observation-box {
      background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
      border: 1px solid #93c5fd;
      border-radius: 8px;
    }
    
    .page-header {
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
      border-radius: 20px;
      border: 1px solid rgba(226, 232, 240, 0.8);
    }
    
    .patient-avatar {
      background: linear-gradient(135deg, var(--color-secondary) 0%, var(--color-secondary-dark) 100%);
      box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
    }
    
    .modal-overlay {
      background: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(4px);
    }
    
    .modal-content {
      background: white;
      border-radius: 20px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
      border: 1px solid rgba(229, 231, 235, 0.8);
    }
  `;

  const uploadedFileUrl = "https://example.com/sample.pdf";

  const handleViewReport = () => {
    setFileUrl(uploadedFileUrl);
    setShowViewReportModal(true);
  };

  const vitalData = [
    {
      icon: <Droplet className="w-5 h-5" />,
      label: "Blood Group",
      value: "O+",
      color: "text-red-600",
    },
    {
      icon: <Heart className="w-5 h-5" />,
      label: "Blood Pressure",
      value: "120/80 mmHg",
      color: "text-green-600",
    },
    {
      icon: <Scale className="w-5 h-5" />,
      label: "Weight",
      value: "62 kg",
      color: "text-blue-600",
    },
    {
      icon: <Ruler className="w-5 h-5" />,
      label: "Height",
      value: "166 cm",
      color: "text-purple-600",
    },
    {
      icon: <Activity className="w-5 h-5" />,
      label: "Pulse",
      value: "72 bpm",
      color: "text-pink-600",
    },
    {
      icon: <Thermometer className="w-5 h-5" />,
      label: "Temperature",
      value: "98.6°C",
      color: "text-orange-600",
    },
  ];

  const testResults = [
    {
      title: "COVID-19 RT-PCR / Antigen Test",
      test: "SARS-CoV-2 RT-PCR",
      result: "Negative",
      status: "safe",
      observation:
        "No detectable viral RNA of COVID-19. Patient is not currently infected with SARS-CoV-2.",
    },
    {
      title: "Throat Swab (for Streptococcus, Influenza)",
      test: "Streptococcus pyogenes & Influenza A/B",
      result: "Influenza A Positive",
      status: "warning",
      observation:
        "Throat swab confirms Influenza A viral infection as a possible cause of fever and cough. No evidence of bacterial streptococcal pharyngitis.",
      details: [
        { label: "Streptococcus pyogenes", value: "Not detected" },
        { label: "Influenza A/B", value: "Influenza A positive" },
      ],
    },
    {
      title: "Malaria Parasite Test",
      test: "Peripheral smear / Rapid Diagnostic Test (RDT)",
      result: "Negative",
      status: "safe",
      observation:
        "No malaria parasites detected. Malaria ruled out as cause of fever.",
    },
    {
      title: "Dengue NS1 Antigen / IgM/IgG Test",
      test: "Dengue Serology Panel",
      result: "All Negative",
      status: "safe",
      observation: "No evidence of acute or past dengue infection.",
      details: [
        { label: "NS1 Antigen", value: "Negative" },
        { label: "IgM", value: "Negative" },
        { label: "IgG", value: "Negative" },
      ],
    },
    {
      title: "Typhoid Test (Widal / TyphiDot)",
      test: "TyphiDot Method",
      result: "IgM and IgG Negative",
      status: "safe",
      observation:
        "Typhoid infection unlikely; serology does not support active or recent infection.",
    },
    {
      title: "Procalcitonin Test",
      test: "Serum Procalcitonin",
      result: "0.12 ng/mL",
      status: "safe",
      observation:
        "Low procalcitonin level suggests viral etiology of fever; no significant bacterial infection detected systemically.",
      reference:
        "Reference range: <0.5 ng/mL = low risk of bacterial infection",
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "safe":
        return "bg-green-100 text-green-800 border-green-200";
      case "warning":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "danger":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Report Viewer

  const fileList = [
    {
      id: 1,
      name: "ClientX_UI_Redesign.pdf",
      size: "4.8 MB",
      url: "/files/ClientX_UI_Redesign.pdf",
    },
    {
      id: 2,
      name: "UI_Header_Design.png",
      size: "2.1 MB",
      url: "../Images/Cardiology.png",
    },
  ];
 if (isLoading || isFetching) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 animate-pulse">
        <div className="max-w-9xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div className="h-10 w-32 bg-gray-300 rounded-lg"></div>
            <div className="h-10 w-24 bg-gray-300 rounded-lg"></div>
          </div>

          {/* Page title */}
          <div className="h-8 w-1/3 bg-gray-300 rounded-lg"></div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 mt-6">
            {/* Main Section */}
            <div className="xl:col-span-3 space-y-8">
              {/* Vitals Card */}
              <div className="p-6 bg-white rounded-lg shadow-md space-y-4">
                <div className="h-6 w-1/4 bg-gray-300 rounded"></div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
                  ))}
                </div>
              </div>

              {/* Test Results */}
              <div className="p-6 bg-white rounded-lg shadow-md space-y-4">
                <div className="h-6 w-1/3 bg-gray-300 rounded"></div>
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Patient Info */}
              <div className="p-6 bg-white rounded-lg shadow-md space-y-4">
                <div className="h-6 w-1/2 bg-gray-300 rounded"></div>
                <div className="w-24 h-24 rounded-full bg-gray-200 mx-auto"></div>
                <div className="h-4 w-1/2 bg-gray-300 mx-auto rounded"></div>
                <div className="h-4 w-1/3 bg-gray-300 mx-auto rounded"></div>

                <div className="space-y-3">
                  <div className="h-10 bg-gray-200 rounded-lg"></div>
                  <div className="h-10 bg-gray-200 rounded-lg"></div>
                </div>
                <div className="h-10 bg-gray-300 rounded-lg"></div>
              </div>

              {/* File Reports */}
              <div className="p-6 bg-white rounded-lg shadow-md space-y-3">
                <div className="h-6 w-1/3 bg-gray-300 rounded"></div>
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="h-10 bg-gray-200 rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">Failed to load Reports details.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white mb-5">
      <style>{gradientStyles}</style>
      <div className="max-w-9xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="page-header p-6 mb-8">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-6">
              <button
               // onClick={handleBack}
                 onClick={() => navigate("/PatientReports")}
                className="p-3 bg-white hover:bg-gray-50 rounded-xl transition-all duration-300 group shadow-md"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 group-hover:text-gray-800" />
              </button>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text mb-2" style={{fontSize:'24px'}}>
                  Reports Overview
                </h1>
                {/* <p className="text-gray-600 text-lg">
                  View comprehensive reports and clinical observations
                </p> */}
              </div>
            </div>
            <button
  onClick={() => navigate(`/EditPatientReport/${patientUserUniqueID}/${appointmentID}`)}
  className="gradient-button font-medium text-center flex justify-content-center align-items-center w-[120px]"
>
  + Edit
</button>

          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-3 space-y-8">
            {/* Vitals Section */}
            <div className="medical-card p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl">
                  <Activity className="w-6 h-6 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Vital Signs
                </h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">

  <div className="vital-card p-4">
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 rounded-lg text-red-600 bg-white shadow-sm">
        <Droplet className="w-5 h-5" />
      </div>
      <span className="text-sm font-semibold text-gray-700">Blood Group</span>
    </div>
    <p className="text-xl font-bold text-gray-900">{data?.labPatientMedicalInfo?.bloodGroup || "Not recorded"}</p>
  </div>

  <div className="vital-card p-4">
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 rounded-lg text-green-600 bg-white shadow-sm">
        <Heart className="w-5 h-5" />
      </div>
      <span className="text-sm font-semibold text-gray-700">Blood Pressure</span>
    </div>
    <p className="text-xl font-bold text-gray-900">{data?.labPatientMedicalInfo?.bloodPressure || "Not recorded"}</p>
  </div>

  <div className="vital-card p-4">
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 rounded-lg text-blue-600 bg-white shadow-sm">
        <Scale className="w-5 h-5" />
      </div>
      <span className="text-sm font-semibold text-gray-700">Weight</span>
    </div>
    <p className="text-xl font-bold text-gray-900"> {data?.labPatientMedicalInfo?.weight ? `${data?.labPatientMedicalInfo.weight} Kg` : "Not recorded"}</p>
  </div>

  <div className="vital-card p-4">
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 rounded-lg text-purple-600 bg-white shadow-sm">
        <Ruler className="w-5 h-5" />
      </div>
      <span className="text-sm font-semibold text-gray-700">Height</span>
    </div>
    <p className="text-xl font-bold text-gray-900">{data?.labPatientMedicalInfo?.height ? `${data?.labPatientMedicalInfo.height} cm` : "Not recorded"}</p>
  </div>

  <div className="vital-card p-4">
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 rounded-lg text-pink-600 bg-white shadow-sm">
        <Activity className="w-5 h-5" />
      </div>
      <span className="text-sm font-semibold text-gray-700">Pulse</span>
    </div>
    <p className="text-xl font-bold text-gray-900">{data?.labPatientMedicalInfo?.pulse ? `${data?.labPatientMedicalInfo.pulse} bpm` : "Not recorded"}</p>
  </div>

  <div className="vital-card p-4">
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 rounded-lg text-orange-600 bg-white shadow-sm">
        <Thermometer className="w-5 h-5" />
      </div>
      <span className="text-sm font-semibold text-gray-700">Temperature</span>
    </div>
    <p className="text-xl font-bold text-gray-900">{data?.labPatientMedicalInfo?.temperature ? `${data?.labPatientMedicalInfo.temperature} °C` : "Not recorded"}</p>
  </div>
</div>
              </div>

            {/* Test Results Section */}
            <div className="medical-card p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Diagnostic Test Summary & Observations
                </h2>
              </div>

              <div className="space-y-6">
                {data?.labReports?.map((test, index) => (
                  <div key={index} onClick={() => setSelectedTest(index)} className="test-card p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {index + 1}. {test.testName}
                        </h3>
                        <p className="text-gray-700 mb-1">
                          <strong>Test:</strong> {test.reportType}
                        </p>
                      </div>
                      {/* <span
                        className={`px-4 py-2 rounded-full text-sm font-semibold ${
                          test.status === "safe"
                            ? "status-safe"
                            : test.status === "warning"
                            ? "status-warning"
                            : "status-danger"
                        }`}
                      >
                        {test.result}
                      </span> */}
                    </div>

                    {/* {test.details && (
                      <div className="mb-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                        <p className="text-sm font-semibold text-gray-800 mb-3">
                          Detailed Results:
                        </p>
                        <div className="space-y-2">
                          {test.details.map((detail, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center p-2 bg-white rounded-lg"
                            >
                              <span className="text-sm text-gray-700 font-medium">
                                {detail.label}:
                              </span>
                              <span className="text-sm font-bold text-gray-900">
                                {detail.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )} */}

                    <div className="observation-box p-4">
                      <p className="text-sm font-semibold text-blue-900 mb-2">
                        Clinical Observation:
                      </p>
                      <div
    className="text-sm text-blue-800 leading-relaxed"
    dangerouslySetInnerHTML={{ __html: test.observationNote || "" }}
  ></div>
                      {test.reference && (
                        <p className="text-xs text-blue-700 mt-2 italic font-medium">
                          {test.reference}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* <div className="mt-6 p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200">
                <p className="text-sm text-emerald-800 font-medium leading-relaxed">
                  <strong>Overall Assessment:</strong> Patient responding well
                  to current medication regimen. Blood pressure has stabilized.
                  Primary concern is Influenza A infection requiring antiviral
                  treatment.
                </p>
              </div> */}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Patient Information */}
            <div className="medical-card p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                  <User className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Patient Information
                </h3>
              </div>

              <div className="text-center mb-6">
                <div className="patient-avatar w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserCircle className="w-16 h-16 text-white" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">{data?.labPatientBasicInfo?.fullName}</h4>
                <p className="text-gray-600 mt-1 font-medium">
                  Patient ID: {data?.labPatientBasicInfo?.patientIDUniqueID}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-4 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl">
                  <Calendar className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="text-sm text-gray-600 font-medium">
                      Age
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      {data?.labPatientBasicInfo?.age}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl">
                  <User className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="text-sm text-gray-600 font-medium">
                      Assigned Doctor
                    </p>
                    <p className="text-sm font-bold text-gray-900">{data?.labPatientBasicInfo?.doctorName}</p>
                  </div>
                </div>
              </div>

              <button className="gradient-dark-button w-full px-4 py-3 rounded-xl font-medium" onClick={()=>navigate(`/ClinicPatientView/${patientUserUniqueID}?from=${encodeURIComponent(
              location.pathname + location.search
            )}`)}>
                View Patient Profile
              </button>
            </div>

            {/* View Reports */}
            <div className="bg-white-100 border rounded-4 p-6">
                <h1 className="text-xl font-bold mb-6">File Reports</h1>
                <ReportViewer files={data?.labReports[selectedTest]?.reportFiles ?? []} />
              </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewReports;
